/**
 * Primitives: buttons, states, sheets and the paywall.
 *
 * Every "state" component here exists because the screen checklist requires one
 * (docs/04-screen-specs.md §I): loading, empty, error and offline are designed states,
 * not afterthoughts, and an empty state must always carry a concrete action.
 */

import type { JSX, ReactNode } from 'react';
import { useEffect } from 'react';

export interface ButtonProps {
  readonly children: ReactNode;
  readonly variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | undefined;
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  readonly onClick?: (() => void) | undefined;
  readonly disabled?: boolean | undefined;
  readonly loading?: boolean | undefined;
  readonly type?: 'button' | 'submit' | undefined;
  readonly fullWidth?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  fullWidth = false,
  ariaLabel,
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={`vl-button vl-button--${variant} vl-button--${size}`}
      data-full-width={fullWidth ? 'true' : 'false'}
      data-loading={loading ? 'true' : 'false'}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
    >
      {loading ? <span className="vl-spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export interface SkeletonProps {
  readonly lines?: number | undefined;
  readonly label?: string | undefined;
}

/** A skeleton, never a spinner on a blank screen. */
export function Skeleton({ lines = 3, label = 'Loading' }: SkeletonProps): JSX.Element {
  return (
    <div className="vl-skeleton" role="status" aria-label={label}>
      {Array.from({ length: lines }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; they never reorder and hold no state.
        <div key={i} className="vl-skeleton__line" />
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly body?: string | undefined;
  /** Required: an empty state must never be a dead end. */
  readonly action: { readonly label: string; readonly onClick: () => void };
}

export function EmptyState({ title, body, action }: EmptyStateProps): JSX.Element {
  return (
    <div className="vl-empty">
      <h3 className="vl-empty__title">{title}</h3>
      {body !== undefined && <p className="vl-empty__body">{body}</p>}
      <Button onClick={action.onClick}>{action.label}</Button>
    </div>
  );
}

export interface ErrorStateProps {
  readonly title: string;
  /** Names the cause, so the learner knows whether retrying is pointless. */
  readonly detail?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
  /** Something useful to do instead, when retrying will not help. */
  readonly alternative?: { readonly label: string; readonly onClick: () => void } | undefined;
}

export function ErrorState({ title, detail, onRetry, alternative }: ErrorStateProps): JSX.Element {
  return (
    <div className="vl-error" role="alert">
      <h3 className="vl-error__title">{title}</h3>
      {detail !== undefined && <p className="vl-error__detail">{detail}</p>}
      <div className="vl-error__actions">
        {onRetry !== undefined && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
        {alternative !== undefined && (
          <Button variant="ghost" onClick={alternative.onClick}>
            {alternative.label}
          </Button>
        )}
      </div>
    </div>
  );
}

export interface BannerProps {
  readonly tone: 'info' | 'success' | 'warning' | 'offline' | 'permission';
  readonly children: ReactNode;
  readonly action?: { readonly label: string; readonly onClick: () => void } | undefined;
  readonly onDismiss?: (() => void) | undefined;
}

export function Banner({ tone, children, action, onDismiss }: BannerProps): JSX.Element {
  return (
    <div className="vl-banner" data-tone={tone} role={tone === 'warning' ? 'alert' : 'status'}>
      <span className="vl-banner__content">{children}</span>
      {action !== undefined && (
        <button type="button" className="vl-link" onClick={action.onClick}>
          {action.label}
        </button>
      )}
      {onDismiss !== undefined && (
        <button type="button" className="vl-icon-button" aria-label="Dismiss" onClick={onDismiss}>
          ✕
        </button>
      )}
    </div>
  );
}

export interface DegradedBannerProps {
  readonly reason: string;
  readonly onRetryVoice?: (() => void) | undefined;
}

/**
 * Shown when a session falls back to text. Phrased so the learner understands the lesson
 * is continuing, which is the whole point of degrading rather than erroring.
 */
export function DegradedBanner({ reason, onRetryVoice }: DegradedBannerProps): JSX.Element {
  const message =
    reason === 'audio_session'
      ? 'Your microphone dropped out, so we switched to text. Your lesson is carrying on.'
      : reason === 'provider_unavailable'
        ? 'Voice is briefly unavailable, so we switched to text. Your lesson is carrying on.'
        : 'Running in text mode for now. Your lesson is carrying on.';

  return (
    <Banner
      tone="warning"
      {...(onRetryVoice !== undefined
        ? { action: { label: 'Try voice again', onClick: onRetryVoice } }
        : {})}
    >
      {message}
    </Banner>
  );
}

export interface SheetProps {
  readonly title: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly size?: 'half' | 'full' | undefined;
}

export function Sheet({ title, children, onClose, size = 'half' }: SheetProps): JSX.Element {
  // Escape closes the sheet. Without this, click-outside is the only dismissal and
  // keyboard users are trapped.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="vl-sheet-backdrop">
      {/* The scrim is a real button, so click-outside is also reachable by keyboard and
          announced, rather than being an invisible click handler on a div. */}
      <button
        type="button"
        className="vl-sheet__scrim"
        aria-label={`Close ${title}`}
        onClick={onClose}
      />
      <div
        className={`vl-sheet vl-sheet--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="vl-sheet__header">
          <h2 className="vl-sheet__title">{title}</h2>
          <button type="button" className="vl-icon-button" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="vl-sheet__body">{children}</div>
      </div>
    </div>
  );
}

export interface Plan {
  readonly id: string;
  readonly name: string;
  readonly priceLabel: string;
  readonly perMonthLabel: string | null;
  readonly trialDays: number;
}

export interface PaywallProps {
  readonly variant: 'primary' | 'comparison' | 'contextual' | 'winback';
  readonly trigger: string;
  readonly headline: string;
  readonly benefits: readonly string[];
  readonly heroPlan: Plan;
  readonly otherPlans: readonly Plan[];
  readonly rating: { readonly stars: number; readonly learners: number } | null;
  readonly testimonial: { readonly quote: string; readonly attribution: string } | null;
  readonly remindBeforeTrial: boolean;
  readonly onToggleReminder: (value: boolean) => void;
  readonly onSelectPlan: (planId: string) => void;
  readonly onViewOtherPlans: () => void;
  readonly onDismiss: () => void;
}

/**
 * The paywall. Deliberate omissions, each one a documented rule
 * (docs/04-screen-specs.md G1): no countdown timer, no fabricated discount, no fake
 * testimonial, and an always-present dismiss that returns to a usable state.
 */
export function Paywall({
  variant,
  trigger,
  headline,
  benefits,
  heroPlan,
  otherPlans,
  rating,
  testimonial,
  remindBeforeTrial,
  onToggleReminder,
  onSelectPlan,
  onViewOtherPlans,
  onDismiss,
}: PaywallProps): JSX.Element {
  return (
    <div
      className="vl-paywall"
      data-variant={variant}
      data-trigger={trigger}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vl-paywall-title"
    >
      <header className="vl-paywall__header">
        <button type="button" className="vl-icon-button" aria-label="Close" onClick={onDismiss}>
          ✕
        </button>
        <button type="button" className="vl-link" onClick={() => onSelectPlan('restore')}>
          Restore
        </button>
      </header>

      <h2 className="vl-paywall__title" id="vl-paywall-title">
        {headline}
      </h2>

      {rating !== null && (
        <p className="vl-paywall__social">
          ★ {rating.stars.toFixed(1)} · {rating.learners.toLocaleString()} learners
        </p>
      )}

      <ul className="vl-paywall__benefits">
        {benefits.map((benefit) => (
          <li key={benefit}>{benefit}</li>
        ))}
      </ul>

      <button
        type="button"
        className="vl-paywall__plan"
        data-hero="true"
        onClick={() => onSelectPlan(heroPlan.id)}
      >
        <span className="vl-paywall__plan-name">{heroPlan.name}</span>
        {heroPlan.trialDays > 0 && (
          <span className="vl-paywall__plan-trial">{heroPlan.trialDays} days free</span>
        )}
        <span className="vl-paywall__plan-price">{heroPlan.priceLabel}</span>
        {heroPlan.perMonthLabel !== null && (
          <span className="vl-paywall__plan-per-month">{heroPlan.perMonthLabel}</span>
        )}
      </button>

      {otherPlans.length > 0 && (
        <button type="button" className="vl-link" onClick={onViewOtherPlans}>
          View other plans
        </button>
      )}

      {/* Honest anchor against the real alternative, rather than a fake strike-through. */}
      <p className="vl-paywall__anchor">A human tutor costs $25–60 an hour.</p>

      {testimonial !== null && (
        <blockquote className="vl-paywall__testimonial">
          <p>{testimonial.quote}</p>
          <footer>{testimonial.attribution}</footer>
        </blockquote>
      )}

      <label className="vl-paywall__reminder">
        <input
          type="checkbox"
          checked={remindBeforeTrial}
          onChange={(event) => onToggleReminder(event.target.checked)}
        />
        Remind me before my trial ends
      </label>

      <p className="vl-paywall__assurance">✓ No payment due now</p>

      <Button fullWidth size="lg" onClick={() => onSelectPlan(heroPlan.id)}>
        {heroPlan.trialDays > 0 ? 'Start free trial' : 'Subscribe'}
      </Button>

      <p className="vl-paywall__cancel">Cancel any time · one tap in Settings</p>
    </div>
  );
}

export interface TrialTimelineProps {
  readonly entries: readonly { readonly kind: string; readonly label: string }[];
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * The dated trial schedule, shown *before* the store sheet. This one screen is our main
 * defence against the refund-anger theme that dominates competitor reviews.
 */
export function TrialTimeline({ entries, onConfirm, onCancel }: TrialTimelineProps): JSX.Element {
  return (
    <div className="vl-trial" role="dialog" aria-modal="true" aria-labelledby="vl-trial-title">
      <h2 className="vl-trial__title" id="vl-trial-title">
        Here&apos;s exactly what happens
      </h2>
      <ol className="vl-trial__list">
        {entries.map((entry) => (
          <li key={entry.kind} data-kind={entry.kind}>
            {entry.label}
          </li>
        ))}
      </ol>
      <p className="vl-trial__cancel">Cancel any time: Settings → Subscription. One tap.</p>
      <Button fullWidth size="lg" onClick={onConfirm}>
        Start free trial
      </Button>
      <Button fullWidth variant="ghost" onClick={onCancel}>
        Not now
      </Button>
    </div>
  );
}

export interface TabBarProps {
  readonly active: 'today' | 'learn' | 'talk' | 'review' | 'me';
  readonly onNavigate: (tab: TabBarProps['active']) => void;
  readonly dueCount?: number | undefined;
}

export function TabBar({ active, onNavigate, dueCount = 0 }: TabBarProps): JSX.Element {
  const tabs: readonly { id: TabBarProps['active']; label: string; glyph: string }[] = [
    { id: 'today', label: 'Today', glyph: '◉' },
    { id: 'learn', label: 'Learn', glyph: '◈' },
    { id: 'talk', label: 'Talk', glyph: '◌' },
    { id: 'review', label: 'Review', glyph: '↻' },
    { id: 'me', label: 'Me', glyph: '◍' },
  ];

  return (
    <nav className="vl-tabbar" aria-label="Main">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className="vl-tabbar__tab"
          data-active={tab.id === active ? 'true' : 'false'}
          aria-current={tab.id === active ? 'page' : undefined}
          aria-label={
            tab.id === 'review' && dueCount > 0 ? `${tab.label}, ${dueCount} cards due` : tab.label
          }
          onClick={() => onNavigate(tab.id)}
        >
          <span aria-hidden="true" className="vl-tabbar__glyph">
            {tab.glyph}
          </span>
          <span className="vl-tabbar__label">{tab.label}</span>
          {tab.id === 'review' && dueCount > 0 && (
            <span className="vl-tabbar__badge" aria-hidden="true">
              {dueCount > 99 ? '99+' : dueCount}
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}
